import { FlexRow, StyledButton } from '@gorila-shared-ui/components';
import { CheckboxOverrides } from 'baseui/checkbox';
import { FilterOverrides } from 'baseui/table';
import { TabOverrides, TabsOverrides } from 'baseui/tabs-motion';
import { CSSProperties } from 'react';
import { themedUseStyletron } from '../theme/customTheme';

export function useStyles() {
  const [css, theme] = themedUseStyletron();

  const fillParent = css({
    width: '100%',
    height: '100%',
  });

  const horizontalFlex = css({
    display: 'flex',
  });

  const verticalFlex = css({
    display: 'flex',
    flexDirection: 'column',
  });

  const centeredFlex = css({
    alignItems: 'center',
    justifyContent: 'center',
  });

  const centeredStart = css({
    alignItems: 'center',
    justifyContent: 'start',
  });

  const centeredEnd = css({
    alignItems: 'center',
    justifyContent: 'end',
  });

  const centeredBetween = css({
    alignItems: 'center',
    justifyContent: 'space-between',
  });

  const flexFill = css({
    flex: '1 1 100%',
  });

  const ellipseText = ({ maxLines = 3, breakAll = false }) =>
    css({
      '-webkit-box-orient': 'vertical',
      '-webkit-line-clamp': maxLines,
      display: '-webkit-box',
      overflow: 'hidden',
      wordBreak: breakAll ? 'break-all' : 'break-word',
    });

  const warn = css({
    color: theme.colors.negative,
  });

  const success = css({
    color: theme.colors.positive,
  });

  const colors = {
    warn,
    success,
  };

  const dataHeaderClass = css({
    paddingTop: theme.sizing.scale200,
    paddingBottom: theme.sizing.scale200,
    paddingLeft: theme.sizing.scale500,
    paddingRight: theme.sizing.scale500,
    backgroundColor: theme.colors.backgroundSecondary,
  });

  const dataContentClass = css({
    paddingLeft: theme.sizing.scale500,
    paddingRight: theme.sizing.scale500,
  });

  const basicPageContainer = css({
    padding: theme.sizing.scale400,
    gap: theme.sizing.scale500,
    boxSizing: 'border-box',
  });

  const classes = {
    fillParent,
    horizontalFlex,
    verticalFlex,
    centeredFlex,
    centeredStart,
    centeredEnd,
    centeredBetween,
    flexFill,
    ellipseText,
    dataHeaderClass,
    dataContentClass,
    basicPageContainer,
  };

  const tabsOverrides: TabsOverrides = {
    Root: {
      style: () => ({
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
      }),
    },
    TabBorder: {
      style: { flexShrink: 0 },
    },
  };

  const tabOverrides: TabOverrides = {
    TabPanel: {
      style: {
        paddingTop: 0,
        paddingRight: 0,
        paddingBottom: 0,
        paddingLeft: 0,
        overflow: 'hidden',
      },
    },
  };

  const tabOverflowOverrides: TabOverrides = {
    TabPanel: {
      style: {
        overflow: 'hidden',
      },
    },
  };

  const smallTableCellStyles: CSSProperties = {
    paddingLeft: theme.sizing.scale300,
    paddingRight: theme.sizing.scale300,
  };

  const smallTableCell: CSSProperties = {
    ...smallTableCellStyles,
    ...theme.typography.ParagraphXSmall,
    paddingTop: theme.sizing.scale100,
    paddingBottom: theme.sizing.scale100,
  };

  const smallTableCellHeader: CSSProperties = {
    ...smallTableCell,
    ...theme.typography.LabelSmall,
    paddingTop: theme.sizing.scale300,
    paddingBottom: theme.sizing.scale300,
  };

  const smallCheckboxOverrides: CheckboxOverrides = {
    Root: {
      style: {
        alignItems: 'center',
      },
    },
    Checkmark: {
      style: {
        width: theme.sizing.scale600,
        height: theme.sizing.scale600,
      },
    },
    Label: {
      style: {
        ...theme.typography.LabelSmall,
      },
    },
  };

  const filtersOverrides: (onResetFilters: () => void) => FilterOverrides = (onResetFilters: () => void) => ({
    Heading: {
      component: () => <></>,
    },
    Footer: {
      component: () => {
        return (
          <FlexRow
            classNames={css({
              justifyContent: 'end',
              padding: theme.sizing.scale200,
            })}
          >
            <StyledButton
              onClick={onResetFilters}
              size="mini"
              kind="tertiary"
            >
              Limpiar filtros
            </StyledButton>
          </FlexRow>
        );
      },
    },
    Content: {
      style: () => ({
        borderTop: 'none',
        minWidth: '200px',
      }),
    },
  });

  const listItemOverrides = ({
    onClick,
    isSelected = false,
    isHover = false,
    minHeight,
  }: {
    onClick: () => void;
    isSelected?: boolean;
    isHover?: boolean;
    minHeight?: number;
  }) => {
    const contentStyle: CSSProperties = {
      marginLeft: 0,
      paddingLeft: theme.sizing.scale600,
    };
    if (minHeight) {
      contentStyle['minHeight'] = `${minHeight}px`;
    }
    return {
      Root: {
        props: {
          onClick,
        },
        style: {
          cursor: 'pointer',
          backgroundColor: isSelected
            ? theme.colors.backgroundTertiary
            : isHover
              ? theme.colors.backgroundSecondary
              : undefined,
        },
      },
      Content: {
        style: contentStyle,
      },
    };
  };

  const overrides = {
    tabsOverrides,
    tabOverrides,
    tabOverflowOverrides,
    smallCheckboxOverrides,
    filtersOverrides,
    listItemOverrides,
  };

  const styles = {
    smallTableCell,
    smallTableCellHeader,
  };

  const concatenateClasses = (...classes: string[]) => {
    return classes.join(' ');
  };

  return { css, theme, classes, colors, overrides, concatenateClasses, styles };
}
